import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";

import { ConfirmDialog, DropdownItem } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import { updateGroupMembers } from "@devices/store";
import { Group } from "@devices/types";
import DeleteGroupDialog from "@groups/components/DeleteGroupDialog";
import EditGroupDialog from "@groups/components/EditGroupDialog";
import { useGroupDeleteGate } from "@groups/hooks/useGroupDeleteGate";
import { DialogContentText } from "@mui/material";
import AuthService from "@services/authService";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const DeleteGroupItem = (props: Group & { setIsDeleteGroupDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["pages"]);

    const { isDeleteDisabled, blockedReason } = useGroupDeleteGate(props.id);

    const handleDeleteGroup = () => {
        props.setIsDeleteGroupDialogOpen(true);
    };

    return (
        <DropdownItem
            disabled={isDeleteDisabled}
            iconName="fa-solid fa-fw fa-trash"
            label={t("pages:groupPage.tooltip.deleteGroup")}
            danger
            onClick={handleDeleteGroup}
            tooltipProps={{ title: blockedReason, hide: !isDeleteDisabled }}
        />
    );
};

const LeaveGroupItem = (props: Group & { setIsLeaveGroupDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["pages", "sidepanels"]);
    const currentUser = AuthService.getCurrentUser();

    const isGroupMember = props.members.some((item) => item.email === currentUser?.email);

    const handleLeaveGroup = () => {
        props.setIsLeaveGroupDialogOpen(true);
    };

    if (!isGroupMember) {
        return (
            <DropdownItem
                label={t("pages:groupPage.tooltip.leaveGroup")}
                iconName="fa-solid fa-fw fa-right-from-bracket"
                danger
                tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.notDirectMemberOfGroup") }}
                disabled
            />
        );
    }

    return (
        <DropdownItem
            label={t("pages:groupPage.tooltip.leaveGroup")}
            iconName="fa-solid fa-fw fa-right-from-bracket"
            danger
            onClick={handleLeaveGroup}
        />
    );
};

export default (props: Group): React.JSX.Element => {
    const [isLeaveGroupDialogOpen, setIsLeaveGroupDialogOpen] = React.useState(false);
    const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = React.useState(false);
    const [isEditGroupDialogOpen, setIsEditGroupDialogOpen] = React.useState(false);

    const dispatch = useDispatch();
    const currentUser = AuthService.getCurrentUser();
    const { t } = useTranslation(["sidepanels", "modals", "common", "pages"]);

    const cancelLeaveGroupDialog = () => {
        if (isLeaveGroupDialogOpen) {
            setIsLeaveGroupDialogOpen(false);
        }
    };
    const leaveGroup = async () => {
        if (currentUser) {
            await dispatch(updateGroupMembers({ id: props.id, removedMembers: [currentUser?.email] }))
                .unwrap()
                .then(() => showSuccessToast(t("sidepanels:groupPanel.toast.memberUpdated")))
                .catch(({ message }) => showErrorToast(message));
        }
    };

    return (
        <React.Fragment>
            <DropdownContainer
                icon={<i className="fa-solid fa-ellipsis-vertical" />}
                tooltipProps={{ title: t("common:tooltip.moreActions") }}
            >
                <DropdownItem
                    label={t("pages:groupPage.button.editGroup")}
                    iconName="fa-solid fa-fw fa-pencil"
                    onClick={() => setIsEditGroupDialogOpen(true)}
                />
                <LeaveGroupItem {...props} setIsLeaveGroupDialogOpen={setIsLeaveGroupDialogOpen} />
                <DeleteGroupItem {...props} setIsDeleteGroupDialogOpen={setIsDeleteGroupDialogOpen} />
            </DropdownContainer>

            <EditGroupDialog group={props} open={isEditGroupDialogOpen} onClose={() => setIsEditGroupDialogOpen(false)} />
            <DeleteGroupDialog group={props} open={isDeleteGroupDialogOpen} onClose={() => setIsDeleteGroupDialogOpen(false)} />

            <ConfirmDialog
                title={t("modals:leaveGroup.title")}
                open={isLeaveGroupDialogOpen}
                onConfirm={leaveGroup}
                onClose={cancelLeaveGroupDialog}
            >
                <DialogContentText>{t("modals:leaveGroup.description")}</DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
